<template>
  <div class="container-fluid">
    <!-- PAGE TITLE HEADER -->
    <page-title :canDownload="false" :title="dashboardHeaderTitle" :isTimelineDisplayed="true" />

    <!-- CHARTS LIST -->
    <charts-list
      v-if="hierarchy[getCurrentUser.role.toLowerCase()]"
      ref="charts-list"
      :chartsProp="dashboard.charts"
      :downloaded="downloaded"
      :isDraggable="true"
      :period="period"
      type="dashboard"
      :hierarchy="hierarchy[getCurrentUser.role.toLowerCase()]"
      v-on:createdChart="onChartCreate"
      v-on:deletedChart="onChartDelete"
      v-on:updatedChart="onChartUpdate"
      v-on:updateChartsRows="onUpdateChartsRows"
    />
  </div>
</template>

<script>
import Vue from 'vue'
import moment from 'moment'
import { utilsMixin } from '../utils/mixins'
import VueScrollTo from 'vue-scrollto'
import hierarchyData from '../assets/config/dashboardChartHierarchy'

//Scroll to
Vue.use(VueScrollTo, {
  container: 'body',
  duration: 500,
  easing: 'ease',
  offset: -100,
  force: true,
  cancelable: true,
  onStart: false,
  onDone: false,
  onCancel: false,
  x: false,
  y: true,
})

export default {
  name: 'Dashboard',
  dependencies: ['dashboardService'],
  mixins: [utilsMixin],
  props: ['period'],
  data() {
    return {
      hierarchy: JSON.parse(JSON.stringify(hierarchyData)),
      moment: moment,
      dashboard: {},
      onOverChart: null,
      downloaded: false,
      timelineId: 'timeline',
      timeout: null,
      newFilters: [],
      dashboardId: this.$route.params.id,
      inputs: [],
      dataPresent: false,
      confirmDeleteBtn: null,
      initialTitle: '',
      previewDataset: {},
      userAgent: navigator.userAgent,
      timeCharts: [],
    }
  },
  components: {
    'page-title': () => import('./utils/PageTitle'),
    'charts-list': () => import('./ChartsList'),
  },
  mounted() {
    // Get dashboard from cache
    let cachedDashboard = this.getDashboardFromCache(this.dashboardId)

    if (cachedDashboard.charts) {
      this.dashboard = JSON.parse(JSON.stringify(cachedDashboard))
    }

    // Fetch dashboard charts for update
    this.fetchDashboardCharts()
  },
  watch: {
    period: {
      handler() {
        if (this.period) {
          let start = this.moment(this.period[0], this.dateFormat).format('YYYY-MM-DD')

          let end = this.moment(this.period[1], this.dateFormat).format('YYYY-MM-DD')

          window.history.pushState(
            null,
            null,
            '/dashboard/' + this.dashboardId + '/start/' + start + '/end/' + end,
          )
        }
      },
      immediate: true,
    },
  },
  computed: {
    isOnlyOneSite: function() {
      let siteIDs = []
      let onlyOne = false
      if (this.dashboard && this.dashboard.charts) {
        this.dashboard.charts.forEach(chart => {
          if (chart.filters) {
            let found = chart.filters.find(f => {
              return f.type === 'site_location'
            })
            if (found) {
              found.values.forEach(id => {
                if (siteIDs.indexOf(id) == -1) {
                  siteIDs.push(id)
                }
              })
            }
          }
        })
      }

      if (siteIDs.length > 1) {
        onlyOne = false
      } else if (siteIDs.length === 0) {
        onlyOne =
          this.getCurrentUser.userRole.name === 'partner' && this.getCurrentUser.sites.length == 1
      } else if (siteIDs.length === 1) {
        onlyOne = true
      }
      return onlyOne
    },
    dashboardSiteIDs: function() {
      let siteIDs = []
      if (this.dashboard && this.dashboard.charts) {
        this.dashboard.charts.forEach(chart => {
          if (chart.filters) {
            let found = chart.filters.find(f => {
              return f.type == 'site_location'
            })
            if (found) {
              found.values.forEach(id => {
                if (siteIDs.indexOf(id) == -1) {
                  siteIDs.push(id)
                }
              })
            }
          }
        })
      }
      return siteIDs
    },
    dashboardHeaderTitle: function() {
      if (
        this.dashboard &&
        this.dashboard.tab &&
        this.dashboard.tab.titles &&
        this.getCurrentUser.userRole.name !== 'partner'
      ) {
        return (
          this.getLocalizedTitle(this.dashboard.tab.titles) +
          ' - ' +
          this.getLocalizedTitle(this.dashboard.titles)
        )
      } else if (
        this.dashboard &&
        this.dashboard.tab &&
        this.dashboard.tab.titles &&
        this.getCurrentUser.userRole.name === 'partner'
      ) {
        return this.getLocalizedTitle(this.dashboard.titles)
      } else {
        return ''
      }
    },
  },
  methods: {
    onChartCreate(chart) {
      this.dashboardService
        .addNewChart(chart)
        .then(data => {
          let newChart = data.result
          let chartsList = this.$refs['charts-list']
          chartsList.setChartDatabaseId(chart.id, newChart.id)
          VueScrollTo.scrollTo(`#row-${newChart.row - 1}`)
        })
        .catch(e => {
          console.error('Error', e)
        })
    },
    onChartUpdate(chart) {
      this.dashboardService
        .updateChart(chart)
        .then(data => {
          let updatedChart = data.result
          VueScrollTo.scrollTo(`#row-${updatedChart.row - 1}`)
        })
        .catch(e => {
          console.error('Error', e)
        })
    },
    onChartDelete(chart) {
      if (!chart.databaseId) {
        return
      }

      this.dashboardService
        .deleteChart(chart.databaseId)
        .then(() => {})
        .catch(e => {
          console.error('Error', e)
        })
    },
    fetchDashboardCharts: function() {
      this.dashboardService
        .getDashboard(this.dashboardId)
        .then(data => {
          this.getCurrentUser.tabs
          data.result.charts.map(chart => (chart.databaseId = chart.id))
          this.dashboard = data.result
          this.processChartTitles()
          this.initialTitle = data.result.title
          this.downloaded = true
          this.$emit('updateSite', this.dashboardSiteIDs)

          this.saveDashboardInCache(this.dashboard)
        })
        .catch(e => {
          console.error('Error', e)
        })
    },
    onSelectedDashboardUpdate: function(updatedDashboard) {
      this.dashboard.title = updatedDashboard.title
    },
    onCancelEditDashboard: function() {
      this.dashboard.title = this.initialTitle
    },
    onConfirmDashboardDelete: function() {
      this.configToDelete = {
        type: 'dashboard',
        id: this.dashboard.id,
      }
      this.$refs.confirmDeleteBtn.click()
    },
    onUpdateChartsRows(updatedCharts) {
      //let dashboard = JSON.parse(JSON.stringify(this.dashboard))
      //dashboard.charts = updatedCharts;
      this.$log.debug('onUpdateChartsRows', updatedCharts)

      updatedCharts = updatedCharts.filter(c => {
        return c.type != 'empty'
      })
      this.dashboard.charts = updatedCharts
      this.dashboardService.updateChartsRows(this.dashboard).catch(error => {
        console.error('Error when updating charts rows', error)
      })
    },
    processChartTitles() {
      this.dashboard.charts.forEach(chart => {
        this.locales.forEach(locale => {
          if (!chart.titles[locale] || chart.titles[locale].trim() === '') {
            chart.titles[locale] = this.generateChartTitle(locale, chart)
          }
        })
      })
    },
    getDashboardFromCache(dashboardId) {
      let cachedDashboard = null

      this.getCurrentUser.tabs.find(tab => {
        cachedDashboard = tab.dashboards.find(dashboard => {
          return dashboard.id == Number(dashboardId)
        })
        return cachedDashboard
      })

      return cachedDashboard
    },
    async saveDashboardInCache(updatedDashboard) {
      try {
        const currentUser = this.getCurrentUser

        let tabIndex = -1
        const tab = currentUser.tabs.find((tab, index) => {
          tabIndex = index
          return tab.id == updatedDashboard.tab_id
        })

        let dashboardIndex = -1

        tab.dashboards.find((dashboard, index) => {
          dashboardIndex = index
          return dashboard.id == updatedDashboard.id
        })

        if (tabIndex >= 0 && dashboardIndex >= 0) {
          currentUser.tabs[tabIndex].dashboards[dashboardIndex] = updatedDashboard
          await this.$store.dispatch('auth/setCurrentUser', currentUser)
        }
      } catch (error) {
        this.$log.error(error)
      }
    },
  },
}
</script>

<style>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
